<template>
  <div class="bg-surface-container rounded-large w-full">
    <div v-if="objective.achievedAt" class="mb-1 flex flex-row items-center gap-1 px-4 pt-2">
      <ObjectiveStatusChip :achieved="!!objective.achievedAt" :archived="!!objective.archivedAt" />
      <div class="-mr-2 ml-auto flex shrink-0 flex-row gap-1 self-start">
        <BaseMenu v-if="menuItems.length > 0" :items="menuItems" v-slot="{ open }">
          <BaseIconButton size="S" icon="more_vert" :force-pressed="open" />
        </BaseMenu>
      </div>
    </div>

    <div v-if="isEditing" class="flex flex-col px-4">
      <div class="rounded-large bg-surface-container-high flex flex-col gap-2 p-4">
        <BaseInputField
          type="text"
          required
          v-model="editableObjective.title"
          :label="$t('objective.create.name')"
        />
        <BaseInputField
          type="multiText"
          v-model="editableDescription"
          :label="$t('objective.create.descriptionLabel')"
        />
        <BaseInputSelect
          v-if="feedbackTopics.length > 0"
          v-model="editableObjective.feedbackTopicId"
          :label="$t('objective.create.feedbackTopic')"
          :items="[
            {
              id: undefined,
              title: t('objective.create.noFeedbackTopic'),
              selected: !editableObjective.feedbackTopicId,
              leadingIcon: 'remove',
            },
            ...selectableFeedbackTopics.map((feedbackTopic) => ({
              id: feedbackTopic.id,
              title: feedbackTopic.title,
              leadingIcon: feedbackTopic.icon,
              selected: feedbackTopic.id === editableObjective.feedbackTopicId,
            })),
          ]"
        />
        <div class="mt-2 flex w-full flex-row justify-end gap-1">
          <BaseTextButton @click="cancelEdit">{{ $t('common.cancel') }}</BaseTextButton>
          <BaseTextButton @click="saveWithDescription" color="filled">{{
            $t('common.save')
          }}</BaseTextButton>
        </div>
      </div>
    </div>

    <div v-else class="px-4" :class="[!objective.achievedAt && 'pt-4']">
      <div class="flex min-h-10 flex-row items-center">
        <h4 class="text-title-large">{{ objective.title }}</h4>
        <div
          v-if="!objective.achievedAt"
          class="-mr-2 ml-auto flex shrink-0 flex-row gap-1 self-start"
        >
          <BaseMenu v-if="menuItems.length > 0" :items="menuItems" v-slot="{ open }">
            <BaseIconButton size="S" icon="more_vert" :force-pressed="open" />
          </BaseMenu>
        </div>
      </div>
      <div v-if="feedbackTopic" class="text-on-surface-variant flex flex-row items-center pb-1">
        <BaseIcon :class="feedbackTopic.icon" size="18" class="mr-0.5">{{
          feedbackTopic.icon
        }}</BaseIcon>
        <span class="text-body-small">
          {{ feedbackTopic.title }}
        </span>
      </div>
      <p v-if="objective.description" class="text-body-medium text-on-surface-variant mt-2">
        {{ objective.description }}
      </p>
    </div>

    <ObjectiveActionsList
      v-if="!objective.isImpulse"
      :is-achieved="!!objective.achievedAt"
      class="px-4"
      :actions="editableObjective.actions"
      :is-editable="controls.showActionsEdit"
      :focus-action-id="focusActionId"
      @add="onAddAction"
      @set-checked="setActionChecked"
      @rename="renameAction"
      @delete="deleteAction"
      @reorder="reorderActions"
      @dirty-changed="actionsDirty = $event"
    />

    <div
      class="border-outline-variant mt-4 flex flex-row items-center gap-2 border-t px-4 pt-4 pb-4"
    >
      <div class="flex grow flex-row items-center gap-3">
        <span class="text-body-small text-on-surface-variant">
          {{ useDisplayRelativeDate(objective.createdAt) }}
          <template v-if="objective.achievedAt">
            · {{ $t('objective.achievedAt') }}
            {{ useDisplayRelativeDate(objective.achievedAt) }}
          </template>
          <template v-if="objective.archivedAt">
            · {{ $t('objective.archivedAt') }}
            {{ useDisplayRelativeDate(objective.archivedAt) }}
          </template>
        </span>
        <div v-if="showAuthor" class="flex min-w-0 items-center gap-1">
          <BaseMemberAvatar size="24" :member="objective.author" />
          <span class="text-body-medium truncate">{{ memberShortName(objective.author) }}</span>
        </div>
      </div>
      <div class="flex flex-row items-center gap-2">
        <BaseTextButton
          v-if="controls.showRestore"
          icon="unarchive"
          color="outlined"
          @click="restoreObjective"
          >{{ $t('objective.restore.fromArchive') }}</BaseTextButton
        >
        <BaseTextButton
          v-if="controls.showArchiveInFooter"
          v-tooltip="archiveHintTooltip"
          icon="archive"
          color="outlined"
          @click="archiveObjective"
          >{{ $t('objective.archive.action') }}</BaseTextButton
        >
        <BaseTextButton
          v-if="controls.showAchieve"
          icon="check"
          color="outlined"
          @click="achieveObjective"
          >{{ $t('objective.achieve.action') }}</BaseTextButton
        >
      </div>
    </div>

    <BaseDialog
      type="basic"
      :title="$t('objective.delete.confirmation')"
      :description="$t('objective.delete.description')"
      :is-open="isOpenDeleteObjectiveDialog"
      @close="closeDeleteObjectiveDialog"
    >
      <template #dismiss>
        <BaseTextButton color="text" @click="closeDeleteObjectiveDialog">{{
          $t('common.cancel')
        }}</BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton color="text" @click="deleteObjective">{{
          $t('objective.delete.action')
        }}</BaseTextButton>
      </template>
    </BaseDialog>
  </div>
</template>
<script setup lang="ts">
  import { computed, onBeforeUnmount, ref, toRef, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useObjective } from '../../composables/use-objective'
  import { getObjectiveControls } from '../../composables/objective-controls'
  import BaseIconButton from '../base/button/icon-button/BaseIconButton.vue'
  import BaseTextButton from '../base/button/text-button/BaseTextButton.vue'
  import BaseMenu from '../base/menu/BaseMenu.vue'
  import type { MenuItemGroup, SingleMenuItem } from '../base/menu/MenuItem'
  import BaseDialog from '../base/dialog/BaseDialog.vue'
  import BaseIcon from '../base/icon/BaseIcon.vue'
  import BaseInputField from '../base/form-fields/BaseInputField.vue'
  import type { Objective } from '../../composables/use-objectives'
  import { withoutCoachOnlyTopics, type FeedbackTopic } from '@/core/models/feedback-topic'
  import { useDisplayRelativeDate } from '../../composables/use-display-future-date'
  import ObjectiveActionsList from './ObjectiveActionsList.vue'
  import ObjectiveStatusChip from './ObjectiveStatusChip.vue'
  import BaseInputSelect from '../base/form-fields/BaseInputSelect.vue'
  import BaseMemberAvatar from '../base/avatar/BaseMemberAvatar.vue'
  import { memberShortName } from '@/core/models/member/member-name'
  import { useUserStore } from '@/stores/user.store'
  import { useUnsavedChangesStore } from '@/stores/unsaved-changes.store'

  type ObjectiveFeedbackTopicOption = Pick<FeedbackTopic, 'id' | 'title' | 'icon' | 'coachOnly'>

  interface Props {
    objective: Objective
    feedbackTopic?: ObjectiveFeedbackTopicOption
    feedbackTopics?: readonly ObjectiveFeedbackTopicOption[]
    isReadOnly?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    isReadOnly: false,
    feedbackTopics: () => [],
  })

  const { t } = useI18n()
  const selectableFeedbackTopics = computed(() => withoutCoachOnlyTopics(props.feedbackTopics))

  const {
    isEditing,
    editableObjective,
    cancelEdit,
    saveObjective,
    editObjective,
    deleteObjective,
    openDeleteObjectiveDialog,
    closeDeleteObjectiveDialog,
    isOpenDeleteObjectiveDialog,
    achieveObjective,
    reopenObjective,
    archiveObjective,
    restoreObjective,
    addAction,
    renameAction,
    setActionChecked,
    deleteAction,
    reorderActions,
  } = useObjective(toRef(props, 'objective'))

  const focusActionId = ref<string>()
  const actionsDirty = ref(false)
  const { setSourceDirty } = useUnsavedChangesStore()

  const syncDirtyState = () => {
    setSourceDirty(props.objective.id, isEditing.value || actionsDirty.value)
  }

  watch([isEditing, actionsDirty], syncDirtyState, { immediate: true })

  onBeforeUnmount(() => {
    setSourceDirty(props.objective.id, false)
  })

  const onAddAction = async () => {
    const id = await addAction()
    if (id) {
      focusActionId.value = id
    }
  }

  const { user } = useUserStore()

  const showAuthor = computed(() => {
    const author = props.objective.author
    return author != null && author.id !== user.id
  })

  const controls = computed(() =>
    getObjectiveControls(props.objective, {
      isReadOnly: props.isReadOnly,
      currentUserId: user.id,
    }),
  )

  const archiveHintTooltip = computed(() => {
    if (controls.value.archiveHint === 'reopen') {
      return t('objective.archive.hint.reopen')
    }
    if (controls.value.archiveHint === 'dialogHistory') {
      return t('objective.archive.hint.dialogHistory')
    }
    return undefined
  })

  watch(
    () => controls.value.showEdit,
    (showEdit) => {
      if (!showEdit) {
        cancelEdit()
      }
    },
  )

  const editableDescription = computed({
    get: () => editableObjective.value.description ?? '',
    set: (value: string) => {
      editableObjective.value.description = value || null
    },
  })

  const saveWithDescription = () => {
    editableObjective.value.description = editableDescription.value || null
    return saveObjective()
  }

  const menuItems = computed(() => {
    const items: MenuItemGroup[] = []
    if (controls.value.showEdit) {
      items.push({
        items: [
          {
            id: 'edit',
            leadingIcon: 'edit',
            title: t('common.edit'),
            callback: () => {
              editObjective()
            },
          },
        ],
      })
    }
    if (controls.value.showRestore) {
      items.push({
        items: [
          {
            id: 'restore',
            leadingIcon: 'unarchive',
            title: t('objective.restore.fromArchive'),
            callback: () => {
              void restoreObjective()
            },
          },
        ],
      })
    } else {
      const lifecycle: SingleMenuItem[] = []
      if (controls.value.showReopen) {
        lifecycle.push({
          id: 'reopen',
          leadingIcon: 'undo',
          title: t('objective.reopen.action'),
          callback: () => {
            void reopenObjective()
          },
        })
      }
      if (controls.value.showAchieve) {
        lifecycle.push({
          id: 'achieve',
          leadingIcon: 'check_circle',
          title: t('objective.achieve.action'),
          callback: () => {
            void achieveObjective()
          },
        })
      }
      if (controls.value.showArchive && !controls.value.showArchiveInFooter) {
        lifecycle.push({
          id: 'archive',
          leadingIcon: 'archive',
          title: t('objective.archive.action'),
          tooltip: archiveHintTooltip.value,
          callback: () => {
            void archiveObjective()
          },
        })
      }
      if (lifecycle.length > 0) {
        items.push({ items: lifecycle })
      }
    }
    if (controls.value.showDelete) {
      items.push({
        items: [
          {
            id: 'delete',
            leadingIcon: 'delete',
            destructive: true,
            title: t('common.delete'),
            callback: () => {
              openDeleteObjectiveDialog()
            },
          },
        ],
      })
    }
    return items
  })
</script>
